import {Component, OnInit} from '@angular/core';
import {DatePipe} from '@angular/common';
import {MatDialogRef} from '@angular/material/dialog';
import {MatRadioChange} from '@angular/material/radio';
import {Subject, catchError, debounceTime, finalize, of, switchMap, take, takeUntil} from 'rxjs';
import {Common} from '../../../../common/util/Util';
import {DisplayStrings} from '../../../../common/constants/DisplayStrings';
import {InsuredPersonStatusDTO} from '../../../models/InsuredPersonStatusDTO';
import {InsuredPersonService} from '../../../../ent/services/insured-person.service';

enum SearchType {
  CardNumber = 'Card Number',
  IdNumber = 'ID Number'
}

@Component({
  selector: 'app-member-lookup-dialog',
  templateUrl: './member-lookup-dialog.component.html',
  styleUrls: [
    './member-lookup-dialog.component.scss',
    '../../../../../../styles/global/_dialog-styles.scss'],
})

export class MemberLookupDialogComponent implements OnInit {
  readonly destroyed$ = new Subject();
  readonly debounceTime = Common.DEBOUNCE_TIME;
  readonly filterDataValueTextChanged$ = new Subject<boolean>();
  readonly searchType = SearchType;

  insuredPerson: InsuredPersonStatusDTO;
  insuredPersonDisplayProperties = [];
  filterValue = '';
  isSearchByCardNumberSelected = true;
  isBusyCounter = 0;
  message: string;
  maticon: string;

  constructor(
    public datePipe: DatePipe,
    public dialogRef: MatDialogRef<MemberLookupDialogComponent>,
    private insuredPersonService: InsuredPersonService,
  ) {
  }

  ngOnInit(): void {
    this.initSubscriptions();
    this.setMessageAndIconValue();
  }

  initSubscriptions(): void {
    this.filterDataValueTextChanged$
      .pipe(
        takeUntil(this.destroyed$),
        debounceTime(this.debounceTime),
        switchMap(() => {
          if (this.filterValue && this.filterValue !== '') {
            this.isBusyCounter++;
            return this.insuredPersonService.getInsuredPersonStatusByCardOrIDNumber(this.filterValue, this.isSearchByCardNumberSelected)
              .pipe(
                take(1),
                finalize(() => this.isBusyCounter--),
                catchError((error) => {
                  return of(null);
                })
              );
          }
          else {
            return of(null);
          }
        }),
      )
      .subscribe((insuredPersonStatusDTO: InsuredPersonStatusDTO) => {
        if (insuredPersonStatusDTO !== null) {
          this.setInsuredPersonDisplayProperties(insuredPersonStatusDTO);
        }
        this.insuredPerson = insuredPersonStatusDTO;
        this.setMessageAndIconValue();
      });
  }

  onInputChangedSearchMembers(event: Event): void {
    this.filterValue = (event.target as HTMLInputElement).value;
    this.filterDataValueTextChanged$.next(true);
  }

  onClickOkay(): void {
    this.dialogRef.close();
  }

  onCardOrIdNumberRadioOptionValueChanged($event: MatRadioChange): void {
    const value = $event.source.value;
    this.isSearchByCardNumberSelected = value === this.searchType.CardNumber;
    this.filterDataValueTextChanged$.next(true);
  }

  private setMessageAndIconValue(): void {
    if (!this.insuredPerson) {
      const isFilterValueEmpty = this.filterValue === '';
      this.maticon = isFilterValueEmpty ? 'warning' : 'error';
      this.message = isFilterValueEmpty ? DisplayStrings.searchErrorMessages.numberBlank : DisplayStrings.searchErrorMessages.memberNotFoundByCardOrId;
    }
  }

  private setInsuredPersonDisplayProperties(insuredPersonStatusDTO: InsuredPersonStatusDTO): void {
    this.insuredPersonDisplayProperties = [
      {name: 'Membership Number', value: insuredPersonStatusDTO.membershipNumber},
      {name: 'First Name', value: insuredPersonStatusDTO.firstName},
      {name: 'Last Name', value: insuredPersonStatusDTO.lastName},
      {name: 'Gender', value: insuredPersonStatusDTO.memberGender},
      {name: 'Dependant Code', value: insuredPersonStatusDTO.dependantCode},
      {name: 'Date Of Birth', value: this.formatDate(insuredPersonStatusDTO.dateOfBirth)},
      {name: 'Benefit Start Date', value: this.formatDate(insuredPersonStatusDTO.benefitStartDate)},
      {name: 'Benefit End Date', value: this.formatDate(insuredPersonStatusDTO.benefitEndDate)},
      {name: 'ID Number', value: insuredPersonStatusDTO.idNumber},
    ];
  }

  private formatDate(date: string): string {
    // date format must be changed from: yyyymmdd to yyyy/mm/dd
    return date && /^[0-9]{8}$/.test(date) ? `${date.substring(0, 4)}/${date.substring(4, 6)}/${date.substring(6, 8)}` : date;
  }
}
